import { Fragment, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { BellIcon, CaretRightIcon, ChecksIcon, TrashIcon } from '@phosphor-icons/react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import classNames from '@utils/classnames';
import { formatDateTime } from '@utils/date';
import { PageHeader } from 'components/PageHeader';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { Button } from 'components/button/Button';
import { EmptyState } from 'components/empty-state/EmptyState';
import { ToggleGroup } from 'components/toggle-group/ToggleGroup';

import { deleteNotification, markNotificationsAsRead } from '../models';
import { useNotificationsCtx } from './context';
import { Notification } from './types';
import { getNotificationAppearance, TONE_CLASS_NAME } from './appearance';
import { getDisplayError } from '../../../utils/get-display-error';

type NotificationFilter = 'all' | 'unread';

const WEEKDAYS = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
const MONTHS = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
];

/** "Vandaag" / "Gisteren" / "woensdag 26 augustus" / "26 augustus 2025". */
function getGroupLabel(createdAt: string): string {
  const date = dayjs(createdAt);
  const daysAgo = dayjs().startOf('day').diff(date.startOf('day'), 'day');

  if (daysAgo <= 0) return 'Vandaag';
  if (daysAgo === 1) return 'Gisteren';

  const dayAndMonth = `${date.date()} ${MONTHS[date.month()]}`;
  if (daysAgo < 7) return `${WEEKDAYS[date.day()]} ${dayAndMonth}`;
  return date.year() === dayjs().year() ? dayAndMonth : `${dayAndMonth} ${date.year()}`;
}

interface INotificationGroup {
  label: string;
  notifications: Notification[];
}

function groupByDay(notifications: Notification[]): INotificationGroup[] {
  const groups: INotificationGroup[] = [];

  for (const notification of notifications) {
    const label = getGroupLabel(notification.createdAt);
    const lastGroup = groups[groups.length - 1];

    if (lastGroup?.label === label) {
      lastGroup.notifications.push(notification);
    } else {
      groups.push({ label, notifications: [notification] });
    }
  }

  return groups;
}

interface INotificationRowProps {
  notification: Notification;
  refresh: () => void;
}

const NotificationRow: React.FC<INotificationRowProps> = (props) => {
  const { notification, refresh } = props;
  const [isDeleting, setIsDeleting] = useState(false);
  const { category, icon: Icon, tone } = getNotificationAppearance(notification);

  return (
    <div className="group relative flex min-h-15 items-center gap-3 border-b border-border bg-surface px-3.5 py-2 transition-colors last:border-b-0 hover:bg-surface-hover">
      <span className="flex h-1.5 w-1.5 shrink-0 items-center justify-center">
        {!notification.isRead && <span className="h-1.5 w-1.5 rounded-full bg-brand" />}
      </span>

      <span
        className={classNames(
          'flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-md',
          TONE_CLASS_NAME[tone],
        )}
      >
        <Icon className="h-4 w-4" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div
          className={classNames(
            'line-clamp-2 wrap-break-word text-sm',
            notification.isRead ? 'font-regular text-fg-secondary' : 'font-medium text-fg',
          )}
        >
          {notification.link ? (
            <Link to={notification.link} className="after:absolute after:inset-0">
              {notification.content}
            </Link>
          ) : (
            notification.content
          )}
        </div>
        <div className="truncate text-2xs text-fg-muted" title={formatDateTime(notification.createdAt)}>
          {category} · {dayjs(notification.createdAt).format('HH:mm')}
        </div>
      </div>

      <div className="relative flex shrink-0 items-center gap-0.5">
        <Button
          color="ghost"
          size={8}
          ariaLabel="Notificatie wissen"
          title="Notificatie wissen"
          isLoading={isDeleting}
          onTrigger={async () => {
            setIsDeleting(true);
            try {
              await deleteNotification(notification.id);
              refresh();
            } catch (err) {
              captureException(err);
              toast.error('Kon notificatie niet wissen: ' + getDisplayError(err));
            } finally {
              setIsDeleting(false);
            }
          }}
        >
          <TrashIcon className="button-icon" />
        </Button>

        {notification.link && <CaretRightIcon className="h-4 w-4 shrink-0 text-border-strong" />}
      </div>
    </div>
  );
};

const NotificationsPage = () => {
  const {
    state: { notifications },
    refresh: refreshNotifications,
  } = useNotificationsCtx();
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [isMarkingRead, setIsMarkingRead] = useState(false);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.isRead).length, [notifications]);
  const groups = useMemo(
    () => groupByDay(filter === 'unread' ? notifications.filter((n) => !n.isRead) : notifications),
    [notifications, filter],
  );

  return (
    <>
      <PageHeader title="Notificaties" />

      <PageHeading
        noMarginBottom
        leftSide={<Breadcrumb currentItem="Notificaties" />}
        rightSide={
          <Button
            color="primary"
            iconLeft={<ChecksIcon className="button-icon" />}
            isDisabled={unreadCount === 0}
            isLoading={isMarkingRead}
            onTrigger={async () => {
              setIsMarkingRead(true);
              try {
                await markNotificationsAsRead(notifications[0]?.createdAt ?? new Date().toISOString());
                refreshNotifications();
              } catch (err) {
                captureException(err);
                toast.error('Kon notificaties niet als gelezen markeren: ' + getDisplayError(err));
              } finally {
                setIsMarkingRead(false);
              }
            }}
          >
            Alles gelezen markeren
          </Button>
        }
      />

      <div className="flex flex-col gap-4 px-7 py-5">
        <div className="flex items-center gap-3">
          <ToggleGroup
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'Alles', count: notifications.length },
              { value: 'unread', label: 'Ongelezen', count: unreadCount },
            ]}
          />

          <p className="ml-auto text-xs text-fg-muted">Ongelezen blijven staan tot je ze hierboven afvinkt</p>
        </div>

        <div className="card">
          {groups.length === 0 ? (
            <EmptyState
              icon={<BellIcon />}
              title={filter === 'unread' ? 'Geen ongelezen notificaties' : 'Geen notificaties'}
              description={
                filter === 'unread'
                  ? 'Je bent helemaal bij.'
                  : 'Nieuwe meldingen over orders, trailers en facturen verschijnen hier.'
              }
            />
          ) : (
            groups.map((group) => (
              <Fragment key={group.label}>
                <div className="flex h-8 items-center border-b border-border bg-surface-sunken px-3.5">
                  <span className="text-2xs font-semibold uppercase tracking-wide text-fg-muted">{group.label}</span>
                </div>

                {group.notifications.map((notification) => (
                  <NotificationRow key={notification.id} notification={notification} refresh={refreshNotifications} />
                ))}
              </Fragment>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationsPage;
